import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../context/AppContext';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '../components/Modal';
import ContractDocument from '../components/ContractDocument';
import { buildContractContext } from '../utils/contractContext';
import { printContract } from '../utils/renderContract';

const AVATAR_COLORS = ['#C8401E', '#1C5FAD', '#1A7A4A', '#B8720A', '#5B3E9E', '#0E7490', '#92400E', '#BE185D'];

export default function CustomerProfileModal() {
  const { modals, closeModal, openModal, customers, addCustomer, rentals, rentalHistory, invoices, vehicles, showToast, resolveAgreementTemplate } = useApp();
  const open = !!modals['customer-profile'];
  const cid = modals['customer-profile-id'];

  const [tab, setTab] = useState('info');
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({});
  const [viewingContract, setViewingContract] = useState(null);  // rental object being viewed
  const [invFilter, setInvFilter] = useState('all');             // invoices tab status filter
  const [invSearch, setInvSearch] = useState('');                // invoices tab text search

  const customer = customers.find(c => c.id === cid);

  useEffect(() => {
    if (open && customer) {
      setF({ ...customer });
      setTab('info');
      setEditing(false);
      setInvFilter('all');
      setInvSearch('');
    }
  }, [open, cid, customer]);

  function close() { closeModal('customer-profile'); }
  function upd(k, v) { setF(p => ({ ...p, [k]: v })); }

  function save() {
    addCustomer(f);
    setEditing(false);
    showToast('Customer updated');
  }

  if (!open) return null;
  if (!customer) return (
    <Modal open={open} onClose={close} size="lg">
      <ModalHeader title="Customer" onClose={close} />
      <ModalBody><div style={{ textAlign: 'center', padding: 32, color: 'var(--tx3)' }}>Customer not found.</div></ModalBody>
    </Modal>
  );

  // Rental history for this customer
  // Collect all rentals (active + completed) for this customer that have a signed contract
  const customerFullName = customer.fname + ' ' + customer.lname;
  const allRentalsForCust = [
    ...rentals.filter(r => r.customer === customerFullName || r.customerId === customer.id || r.customer === customer.company),
    ...rentalHistory.filter(r => r.customer === customerFullName || r.customerId === customer.id || r.customer === customer.company),
  ];
  const custContracts = allRentalsForCust.filter(r =>
    r.signed || r.agreementSaved || r.signData || r.presignData || r.signatureDataUrl
  );

  const activeRentals = rentals.filter(r => r.customer === customer.fname + ' ' + customer.lname ||
    r.customer === customer.fname + ' ' + customer.lname.trim());
  const history = rentalHistory.filter(r => r.customer === customer.fname + ' ' + customer.lname);
  const custInvoices = invoices.filter(i => i.customer === customer.fname + ' ' + customer.lname);
  const totalSpent = custInvoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.total, 0);
  const initials = (customer.fname[0] || '') + (customer.lname[0] || '');

  return (
    <Modal open={open} onClose={close} size="lg">
      <ModalHeader
        icon={
          <div style={{
            width: 28, height: 28, borderRadius: '50%', background: customer.color || 'var(--ac)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 11, fontWeight: 700,
          }}>{initials.toUpperCase()}</div>
        }
        title={customer.fname + ' ' + customer.lname}
        subtitle={customer.nationality + ' · Customer since ' + customer.since}
        onClose={close}
        extra={
          !editing
            ? <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>Edit</button>
            : null
        }
      />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--bd)', padding: '0 16px' }}>
        {['info', 'rentals', 'invoices', 'contracts'].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '8px 14px', fontSize: 12, fontWeight: 500, border: 'none', background: 'none',
              cursor: 'pointer', borderBottom: `2px solid ${tab === t ? 'var(--ac)' : 'transparent'}`,
              color: tab === t ? 'var(--ac)' : 'var(--tx3)',
            }}
          >
            {t === 'info' ? 'Profile'
              : t === 'rentals'   ? `Rentals (${activeRentals.length + history.length})`
              : t === 'invoices'  ? `Pro Formas (${custInvoices.length})`
              : `Contracts (${custContracts.length})`}
          </button>
        ))}
      </div>

      <ModalBody>
        {tab === 'info' && (
          <div>
            {/* Summary stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
              {[
                { label: 'Active Rentals', value: activeRentals.length, color: 'var(--gr)' },
                { label: 'Total Rentals', value: activeRentals.length + history.length },
                { label: 'Total Spent', value: `€${totalSpent.toFixed(0)}`, color: 'var(--ac)' },
              ].map(s => (
                <div key={s.label} style={{ background: 'var(--sur2)', borderRadius: 'var(--r)', padding: '8px 10px', border: '1px solid var(--bd)' }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: s.color || 'var(--tx1)' }}>{s.value}</div>
                  <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {editing ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="fg"><label className="fl">First Name</label>
                  <input className="fi" value={f.fname} onChange={e => upd('fname', e.target.value)} />
                </div>
                <div className="fg"><label className="fl">Last Name</label>
                  <input className="fi" value={f.lname} onChange={e => upd('lname', e.target.value)} />
                </div>
                <div className="fg"><label className="fl">Phone</label>
                  <input className="fi" value={f.phone} onChange={e => upd('phone', e.target.value)} />
                </div>
                <div className="fg"><label className="fl">Email</label>
                  <input className="fi" value={f.email} onChange={e => upd('email', e.target.value)} />
                </div>
                <div className="fg" style={{ gridColumn: '1 / -1' }}>
                  <label className="fl">Address</label>
                  <input className="fi" value={f.address} onChange={e => upd('address', e.target.value)} />
                </div>
                <div className="fg"><label className="fl">Passport / ID</label>
                  <input className="fi" value={f.passport} onChange={e => upd('passport', e.target.value)} />
                </div>
                <div className="fg"><label className="fl">Nationality</label>
                  <input className="fi" value={f.nationality} onChange={e => upd('nationality', e.target.value)} />
                </div>
                <div className="fg"><label className="fl">Licence No.</label>
                  <input className="fi" value={f.licence} onChange={e => upd('licence', e.target.value)} />
                </div>
                <div className="fg"><label className="fl">Licence Expiry</label>
                  <input className="fi" type="date" value={f.licExpiry} onChange={e => upd('licExpiry', e.target.value)} />
                </div>
                <div className="fg" style={{ gridColumn: '1 / -1' }}>
                  <label className="fl">Notes</label>
                  <textarea className="fi" rows={2} style={{ resize: 'vertical' }} value={f.notes} onChange={e => upd('notes', e.target.value)} />
                </div>
                <div className="fg" style={{ gridColumn: '1 / -1' }}>
                  <label className="fl">Avatar Color</label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                    {AVATAR_COLORS.map(c => (
                      <div key={c} onClick={() => upd('color', c)} style={{
                        width: 22, height: 22, borderRadius: '50%', background: c, cursor: 'pointer',
                        border: f.color === c ? '3px solid var(--tx1)' : '2px solid transparent',
                        boxSizing: 'border-box',
                      }} />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12 }}>
                {[
                  ['Phone', customer.phone],
                  ['Email', customer.email],
                  ['Address', customer.address],
                  ['Nationality', customer.nationality],
                  ['Passport / ID', customer.passport],
                  ['Licence No.', customer.licence || '—'],
                  ['Licence Expiry', customer.licExpiry || '—'],
                  ['Customer Since', customer.since],
                ].map(([label, value]) => (
                  <div key={label} style={{ background: 'var(--sur2)', borderRadius: 'var(--r)', border: '1px solid var(--bd)', padding: '6px 10px' }}>
                    <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{label}</div>
                    <div style={{ fontWeight: 500, marginTop: 1 }}>{value}</div>
                  </div>
                ))}
                {customer.notes && (
                  <div style={{ gridColumn: '1 / -1', background: 'var(--am-l)', border: '1px solid var(--am-b)', borderRadius: 'var(--r)', padding: '6px 10px', fontSize: 11, color: 'var(--am)' }}>
                    📝 {customer.notes}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {tab === 'rentals' && (
          <div>
            {activeRentals.length === 0 && history.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: 'var(--tx3)', fontSize: 12 }}>No rental history</div>
            ) : (
              <>
                {activeRentals.map(r => {
                  const v = vehicles.find(veh => veh.id === r.vid);
                  return (
                    <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, padding: '8px 10px', border: '1px solid var(--bd)', borderRadius: 'var(--r)', marginBottom: 6, background: 'var(--sur2)' }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{v?.icon} {v?.name || r.vid}</div>
                        <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{r.id} · Return: {r.endLabel}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gr)' }}>€{r.agreedPrice}</div>
                        <div style={{ fontSize: 9, padding: '2px 6px', borderRadius: 10, background: r.status === 'active' ? 'var(--gr-l)' : r.status === 'overdue' ? 'var(--ac-l)' : 'var(--am-l)', color: r.status === 'active' ? 'var(--gr)' : r.status === 'overdue' ? 'var(--ac)' : 'var(--am)', fontWeight: 600, textTransform: 'uppercase' }}>
                          {r.status}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {history.map(r => {
                  const v = vehicles.find(veh => veh.id === r.vid);
                  return (
                    <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, padding: '8px 10px', border: '1px solid var(--bd)', borderRadius: 'var(--r)', marginBottom: 6, opacity: 0.7 }}>
                      <div>
                        <div style={{ fontSize: 12 }}>{v?.icon} {v?.name || r.vid}</div>
                        <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{r.id} · Returned: {r.endLabel}</div>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--tx3)' }}>€{r.agreedPrice}</div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {tab === 'invoices' && (
          <div>
            {custInvoices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: 'var(--tx3)', fontSize: 12 }}>No pro formas</div>
            ) : (() => {
              const paidList = custInvoices.filter(i => i.status === 'paid');
              const pendingList = custInvoices.filter(i => i.status === 'pending' || i.status === 'issued');
              const overdueList = custInvoices.filter(i => i.status === 'overdue');
              const draftList = custInvoices.filter(i => i.status === 'draft');
              const paidAmount = paidList.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
              const unpaidCount = custInvoices.length - paidList.length;
              const outstanding = custInvoices.filter(i => i.status !== 'paid').reduce((s, i) => s + (parseFloat(i.total) || 0), 0);

              // Apply status + search filter
              const q = invSearch.trim().toLowerCase();
              const shown = custInvoices.filter(inv => {
                const statusOk = invFilter === 'all'
                  || (invFilter === 'pending' && (inv.status === 'pending' || inv.status === 'issued'))
                  || inv.status === invFilter;
                if (!statusOk) return false;
                if (!q) return true;
                return (inv.num || '').toLowerCase().includes(q)
                  || (inv.vehicle || '').toLowerCase().includes(q)
                  || (inv.contract || inv.contractId || '').toLowerCase().includes(q);
              });

              const filterPills = [
                { val: 'all',     label: `All (${custInvoices.length})` },
                { val: 'paid',    label: `Paid (${paidList.length})` },
                { val: 'pending', label: `Pending (${pendingList.length})` },
                { val: 'overdue', label: `Overdue (${overdueList.length})` },
                { val: 'draft',   label: `Drafts (${draftList.length})` },
              ];

              return (
                <>
                  <div style={{
                    display: 'flex', gap: 10, padding: '10px 12px', marginBottom: 10,
                    background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 8, fontSize: 12,
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, color: 'var(--tx3)' }}>Paid</div>
                      <div style={{ fontWeight: 700, color: 'var(--gr)', fontSize: 14 }}>€{paidAmount.toFixed(2)}</div>
                      <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 1 }}>{paidList.length} of {custInvoices.length}</div>
                    </div>
                    <div style={{ flex: 1, borderLeft: '1px solid var(--bl-b)', paddingLeft: 10 }}>
                      <div style={{ fontSize: 10, color: 'var(--tx3)' }}>Unpaid</div>
                      <div style={{ fontWeight: 700, color: unpaidCount > 0 ? 'var(--am)' : 'var(--gr)', fontSize: 14 }}>{unpaidCount}</div>
                      <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 1 }}>pro forma{unpaidCount === 1 ? '' : 's'}</div>
                    </div>
                    <div style={{ flex: 1, borderLeft: '1px solid var(--bl-b)', paddingLeft: 10 }}>
                      <div style={{ fontSize: 10, color: 'var(--tx3)' }}>Outstanding</div>
                      <div style={{ fontWeight: 700, color: outstanding > 0 ? 'var(--am)' : 'var(--gr)', fontSize: 14 }}>€{outstanding.toFixed(2)}</div>
                    </div>
                    {overdueList.length > 0 && (
                      <div style={{ flex: 1, borderLeft: '1px solid var(--bl-b)', paddingLeft: 10 }}>
                        <div style={{ fontSize: 10, color: 'var(--tx3)' }}>Overdue</div>
                        <div style={{ fontWeight: 700, color: 'var(--ac)', fontSize: 14 }}>{overdueList.length}</div>
                        <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 1 }}>pro forma{overdueList.length === 1 ? '' : 's'}</div>
                      </div>
                    )}
                  </div>

                  {/* Filter pills + search */}
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {filterPills.map(opt => (
                      <button
                        key={opt.val}
                        type="button"
                        onClick={() => setInvFilter(opt.val)}
                        className={invFilter === opt.val ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
                        style={{ fontSize: 11 }}
                      >
                        {opt.label}
                      </button>
                    ))}
                    <input
                      type="text"
                      placeholder="Search pro forma #, vehicle, contract…"
                      value={invSearch}
                      onChange={e => setInvSearch(e.target.value)}
                      style={{
                        flex: '1 1 160px', minWidth: 140, marginLeft: 'auto',
                        padding: '4px 8px', fontSize: 11,
                        border: '1px solid var(--bd)', borderRadius: 6, background: 'var(--sur)',
                      }}
                    />
                  </div>

                  {shown.length === 0 && (
                    <div style={{ textAlign: 'center', padding: 18, color: 'var(--tx3)', fontSize: 12, background: 'var(--sur2)', border: '1px dashed var(--bd)', borderRadius: 8 }}>
                      No pro formas match this filter
                    </div>
                  )}
                  {shown.map(inv => (
                <div
                  key={inv.id}
                  onClick={() => { close(); openModal('view-invoice', inv.id); }}
                  style={{
                    display: 'grid', gridTemplateColumns: '1fr auto', gap: 8,
                    padding: '8px 10px', border: '1px solid var(--bd)', borderRadius: 'var(--r)',
                    marginBottom: 6, background: 'var(--sur2)',
                    cursor: 'pointer', transition: 'all .12s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--ac-l)'; e.currentTarget.style.borderColor = 'var(--ac-b)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--sur2)'; e.currentTarget.style.borderColor = 'var(--bd)'; }}
                  title="Click to open pro forma"
                >
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{inv.num} <span style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 400 }}>›</span></div>
                    <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{inv.vehicle} · {inv.issue}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <div style={{ fontSize: 11, fontWeight: 600 }}>€{inv.total.toFixed(2)}</div>
                    <div style={{
                      fontSize: 9, padding: '2px 6px', borderRadius: 10, fontWeight: 600,
                      background: inv.status === 'paid' ? 'var(--gr-l)' : inv.status === 'overdue' ? 'var(--ac-l)' : 'var(--am-l)',
                      color: inv.status === 'paid' ? 'var(--gr)' : inv.status === 'overdue' ? 'var(--ac)' : 'var(--am)',
                      textTransform: 'uppercase',
                    }}>
                      {inv.status}
                    </div>
                  </div>
                </div>
              ))}
                </>
              );
            })()}
          </div>
        )}

        {tab === 'contracts' && (
          <div>
            {custContracts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: 'var(--tx3)', fontSize: 12 }}>
                No signed contracts yet
              </div>
            ) : (
              custContracts.map(r => {
                const v = vehicles.find(vv => vv.id === r.vid);
                const isLease = r.type === 'lease';
                const sigData = r.signatureDataUrl || r.presignData || r.signData || r.agreementSaved?.signatureDataUrl;
                return (
                  <div
                    key={r.id}
                    onClick={() => setViewingContract(r)}
                    style={{
                      display: 'flex', gap: 10, padding: '10px 12px',
                      border: '1px solid var(--bd)', borderRadius: 'var(--r)',
                      marginBottom: 6, background: 'var(--sur2)',
                      cursor: 'pointer', transition: 'all .12s',
                      alignItems: 'center',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--ac-l)'; e.currentTarget.style.borderColor = 'var(--ac-b)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'var(--sur2)'; e.currentTarget.style.borderColor = 'var(--bd)'; }}
                  >
                    <div style={{ fontSize: 22 }}>{isLease ? '📅' : '📋'}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>
                        {r.id} · {isLease ? `Lease (${r.termMonths || 0} months)` : 'Short-term rental'}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 1 }}>
                        {v?.name || r.vid} · {r.startLabel || r.start?.slice(0, 10) || '—'}
                        {r.endLabel ? ` → ${r.endLabel}` : ''}
                      </div>
                    </div>
                    {sigData && (
                      <span style={{ fontSize: 9, padding: '3px 8px', borderRadius: 10, background: 'var(--gr-l)', color: 'var(--gr)', fontWeight: 600 }}>
                        ✓ SIGNED
                      </span>
                    )}
                    <span style={{ fontSize: 12, color: 'var(--tx3)' }}>›</span>
                  </div>
                );
              })
            )}
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        {editing ? (
          <>
            <button className="btn btn-ghost" onClick={() => { setF({ ...customer }); setEditing(false); }}>Cancel</button>
            <button className="btn btn-primary" onClick={save}>Save Changes</button>
          </>
        ) : (
          <button className="btn btn-ghost" onClick={close}>Close</button>
        )}
      </ModalFooter>

      {/* ── Contract viewer overlay with Print ────────────────────── */}
      {viewingContract && (() => {
        const r = viewingContract;
        const v = vehicles.find(vv => vv.id === r.vid);
        const isLease = r.type === 'lease';
        const company = JSON.parse(localStorage.getItem('fleetrent_v1') || '{}').companySettings || {};
        // Resolve template (saved snapshot wins; otherwise live-resolve current template)
        const liveTmpl = (!r.agreementSaved?.body && resolveAgreementTemplate)
          ? resolveAgreementTemplate(r.vid, isLease ? 'lease' : 'rental')
          : null;
        const tmpl = r.agreementSaved?.body
          ? { body: r.agreementSaved.body, clauses: r.agreementSaved.clauses || [], name: r.agreementSaved.templateName }
          : liveTmpl;
        // Build context: use saved snapshot context if present, else build fresh
        const ctx = r.agreementSaved?.context || buildContractContext({
          rental: r,
          vehicle: v,
          customer: customer,
          company,
          signatureDataUrl: r.signatureDataUrl || r.presignData || r.signData,
          signedAt: r.signedAt || r.presignedAt,
        });
        const renderedHtml = r.agreementSaved?.renderedHtml;  // pre-rendered snapshot
        function doPrint() {
          printContract(tmpl || { body: renderedHtml }, ctx, { title: `Contract ${r.id}` });
        }
        return createPortal(
          <div
            onClick={() => setViewingContract(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            >
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--bd)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>
                    {isLease ? '📅 Long-term Lease Agreement' : '📋 Rental Agreement'} · {r.id}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 2 }}>
                    {r.customer} · {v?.name || r.vid} · {r.startLabel}{r.endLabel ? ` → ${r.endLabel}` : ''}
                  </div>
                </div>
                <button onClick={() => setViewingContract(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--tx3)' }}>×</button>
              </div>
              <div style={{ flex: 1, padding: 24, overflowY: 'auto', background: 'var(--sur2)' }}>
                <div style={{ maxWidth: 720, margin: '0 auto' }}>
                  {/* Rendered contract — saved snapshot or live-render */}
                  <ContractDocument
                    template={tmpl}
                    context={ctx}
                    prerenderedHtml={renderedHtml}
                    paperLook={true}
                  />
                </div>
                <div style={{ maxWidth: 720, margin: '14px auto 0', background: '#fff', padding: 18, borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)' }}>
                  {/* Legacy variables kept only so the unchanged linked-invoices / notes blocks below still compile.
                      They have no effect on the new ContractDocument rendering above. */}
                  {(() => {
                    const ag = r.agreementSaved || {};
                    const renterName = ag.customerName || r.customer || (customer.fname + ' ' + customer.lname);
                    const renterPassport = ag.passport || r.passport || customer.passport;
                    const renterLicence = ag.licence || r.licence || customer.licence;
                    const renterEmail = r.email || customer.email;
                    const renterPhone = r.phone || customer.phone;
                    const vehicleName = ag.vehicleName || v?.name || r.vid;
                    const vehiclePlate = ag.vehiclePlate || v?.plate;
                    const startLabel = ag.startLabel || r.startLabel;
                    const endLabel = ag.endLabel || r.endLabel;
                    const t1 = ag.t1 || r.t1;
                    const t2 = ag.t2 || r.t2;
                    const days = ag.days || r.calSpan || r.days;
                    const rate = ag.rate || r.rate;
                    const insuranceName = ag.insuranceName || r.insurance;
                    const insuranceExcess = ag.insuranceExcess || r.insuranceExcess;
                    const extras = ag.extras || r.extras || [];
                    const extrasList = Array.isArray(extras)
                      ? extras.map(e => typeof e === 'string' ? e : (e?.name || e?.label || '')).filter(Boolean)
                      : [];
                    const agreedPrice = ag.agreedPrice || r.agreedPrice;
                    const clauses = [];
                    const bodyText = '';
                    const signedAtFormatted = ag.signedAtFormatted
                      || (r.signedAt ? new Date(r.signedAt).toLocaleString('en-GB') : '');

                    return (
                      <>
                        {/* Suppress unused var warnings for the legacy locals above */}
                        {false && [renterName, renterPassport, renterLicence, renterEmail, renterPhone, vehicleName, vehiclePlate, startLabel, endLabel, t1, t2, days, rate, insuranceName, insuranceExcess, extrasList, agreedPrice, bodyText, clauses, signedAtFormatted]}

                        {/* Linked invoices —
                              • 1 invoice → simple inline row
                              • >1 invoices → summary card + "View All Invoices" button */}
                        {(() => {
                          const linked = invoices.filter(inv => inv.contract === r.id || inv.contractId === r.id);
                          if (linked.length === 0) return null;

                          // Single-invoice case → inline clickable row
                          if (linked.length === 1) {
                            const inv = linked[0];
                            return (
                              <div style={{ marginTop: 20 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8, color: '#333' }}>
                                  📄 Pro Forma
                                </div>
                                <button
                                  type="button"
                                  onClick={() => { setViewingContract(null); close(); openModal('view-invoice', inv.id); }}
                                  style={{
                                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    background: '#f8f8f8', border: '1px solid #e5e5e5', borderRadius: 7,
                                    padding: '10px 14px', cursor: 'pointer', textAlign: 'left',
                                    transition: 'all .12s',
                                  }}
                                  onMouseEnter={e => { e.currentTarget.style.background = '#fff5f0'; e.currentTarget.style.borderColor = '#fab5a0'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = '#f8f8f8'; e.currentTarget.style.borderColor = '#e5e5e5'; }}
                                >
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "'DM Mono',monospace" }}>{inv.num}</div>
                                    <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                                      €{parseFloat(inv.total || 0).toFixed(2)} ·{' '}
                                      <span style={{ fontWeight: 600, color: inv.status === 'paid' ? '#15803d' : inv.status === 'overdue' ? '#b91c1c' : '#b45309' }}>
                                        {inv.status?.charAt(0).toUpperCase() + inv.status?.slice(1)}
                                      </span>
                                      {inv.issued ? ` · ${inv.issued}` : ''}
                                    </div>
                                  </div>
                                  <span style={{ fontSize: 14, color: '#888', marginLeft: 10 }}>›</span>
                                </button>
                              </div>
                            );
                          }

                          // Multi-invoice case → summary + "View All Invoices" button
                          const paidList = linked.filter(i => i.status === 'paid');
                          const paidCount = paidList.length;
                          const unpaidCount = linked.length - paidCount;
                          const paidAmount = paidList.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
                          const pendingTotal = linked
                            .filter(i => i.status !== 'paid')
                            .reduce((s, i) => s + (parseFloat(i.total) || 0), 0);

                          return (
                            <div style={{ marginTop: 20 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8, color: '#333' }}>
                                📄 {isLease ? 'Lease Pro Formas' : 'Pro Formas'} · {linked.length}
                              </div>
                              <div style={{
                                display: 'flex', gap: 12, padding: '10px 12px',
                                background: '#eef4ff', border: '1px solid #c7d8f4', borderRadius: 8,
                                fontSize: 12, marginBottom: 10,
                              }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 10, color: '#666' }}>Paid</div>
                                  <div style={{ fontWeight: 700, color: '#15803d', fontSize: 15 }}>€{paidAmount.toFixed(2)}</div>
                                  <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>{paidCount} of {linked.length}</div>
                                </div>
                                <div style={{ flex: 1, borderLeft: '1px solid #c7d8f4', paddingLeft: 12 }}>
                                  <div style={{ fontSize: 10, color: '#666' }}>Unpaid</div>
                                  <div style={{ fontWeight: 700, color: unpaidCount > 0 ? '#b45309' : '#15803d', fontSize: 15 }}>{unpaidCount}</div>
                                  <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>pro forma{unpaidCount === 1 ? '' : 's'}</div>
                                </div>
                                <div style={{ flex: 1, borderLeft: '1px solid #c7d8f4', paddingLeft: 12 }}>
                                  <div style={{ fontSize: 10, color: '#666' }}>Outstanding</div>
                                  <div style={{ fontWeight: 700, color: pendingTotal > 0 ? '#b45309' : '#15803d', fontSize: 15 }}>€{pendingTotal.toFixed(2)}</div>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => { setViewingContract(null); close(); openModal('lease-invoices', r.id); }}
                                className="btn btn-primary"
                                style={{ width: '100%' }}
                              >
                                📊 View All Pro Formas
                              </button>
                            </div>
                          );
                        })()}

                        {/* Notes (operator notes about the rental — not part of the legal contract) */}
                        {r.notes && (
                          <div style={{ fontSize: 12, color: '#444', marginTop: 16, padding: '10px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6 }}>
                            <strong>Notes:</strong> {r.notes}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
              <div style={{ padding: '12px 18px', borderTop: '1px solid var(--bd)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="btn btn-ghost" onClick={() => setViewingContract(null)}>Close</button>
                <button className="btn btn-primary" onClick={doPrint}>🖨️ Print / PDF</button>
              </div>
            </div>
          </div>,
          document.body
        );
      })()}
    </Modal>
  );
}
